import { useEffect, useState } from "react";
import { SectionShell } from "./SectionShell";
import { MetricCard, DetailRow } from "@/components/MetricCard";
import { AlertsStrip } from "@/components/AlertsStrip";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { loadMetrics, type Metrics } from "@/services/dashboardMetrics";
import { loadAlerts, type Alert } from "@/services/alerts";
import { formatMoney, formatNumber } from "@/lib/format";

/**
 * The overview.
 *
 * Two of these tiles are the ones worth having. **Scenes per lesson** separates
 * somebody trying the product from somebody teaching with it, and a rising
 * lesson count can hide a falling one of those. **The alerts strip** is absent
 * whenever there is nothing wrong, so that the morning it appears, it is a
 * change in the shape of the page rather than a number in a box nobody reads.
 */
export function DashboardPage() {
  const t = useT();
  const { locale } = useI18n();
  const { showError } = useToast();

  const [metrics, setMetrics] = useState<Metrics | undefined>(undefined);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();

    Promise.all([loadMetrics(now), loadAlerts(now, 0)])
      .then(([loadedMetrics, loadedAlerts]) => {
        if (cancelled) return;
        setMetrics(loadedMetrics);
        setAlerts(loadedAlerts);
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoading(false);
        showError(error);
      });

    return () => {
      cancelled = true;
    };
  }, [showError]);

  if (loading) {
    return (
      <SectionShell titleKey="dashboard.title">
        <p className="text-sm text-ink-muted">{t("common.loading")}</p>
      </SectionShell>
    );
  }

  if (!metrics) {
    return (
      <SectionShell titleKey="dashboard.title">
        <p className="text-sm text-ink-muted">{t("error.network.unavailable")}</p>
      </SectionShell>
    );
  }

  const number = (value: number) => formatNumber(locale, value);
  const money = (value: number) => formatMoney(locale, Math.round(value * 100), "EUR");

  return (
    <SectionShell titleKey="dashboard.title">
      <AlertsStrip alerts={alerts} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <MetricCard
          labelKey="metrics.institutions"
          value={number(metrics.institutions.total)}
          detailKey="metrics.institutions.detail"
        >
          <DetailRow labelKey="metrics.institutions.active" value={number(metrics.institutions.active)} />
          <DetailRow
            labelKey="metrics.institutions.suspended"
            value={number(metrics.institutions.suspended)}
          />
          <DetailRow labelKey="metrics.institutions.lapsed" value={number(metrics.institutions.lapsed)} />
          <p className="mt-4 field-label">{t("metrics.institutions.byCountry")}</p>
          {metrics.institutions.byCountry.map((entry) => (
            <div
              key={entry.country}
              className="flex items-baseline justify-between border-b border-paper-warm py-2 text-sm last:border-0"
            >
              <span className="text-ink-muted">{entry.country}</span>
              <span className="text-ink">{number(entry.count)}</span>
            </div>
          ))}
        </MetricCard>

        <MetricCard
          labelKey="metrics.seats"
          value={number(metrics.seats.sold)}
          detailKey="metrics.seats.detail"
        >
          {metrics.seats.byTier.map((entry) => (
            <div
              key={entry.tier}
              className="flex items-baseline justify-between border-b border-paper-warm py-2 text-sm last:border-0"
            >
              <span className="text-ink-muted">{entry.tier}</span>
              <span className="text-ink">
                {number(entry.sold)}
                <span className="ml-3 text-ink-faint">{number(entry.institutions)}</span>
              </span>
            </div>
          ))}
        </MetricCard>

        <MetricCard
          labelKey="metrics.sessions"
          value={number(metrics.sessions.month)}
          detailKey="metrics.sessions.detail"
        >
          <DetailRow labelKey="metrics.sessions.week" value={number(metrics.sessions.week)} />
          <DetailRow labelKey="metrics.sessions.month" value={number(metrics.sessions.month)} />
          <DetailRow labelKey="metrics.sessions.online" value={number(metrics.sessions.online)} />
          <DetailRow labelKey="metrics.sessions.offline" value={number(metrics.sessions.offline)} />
        </MetricCard>

        <MetricCard
          labelKey="metrics.runsPerSession"
          value={number(Math.round(metrics.runsPerSession.average * 10) / 10)}
          detailKey="metrics.runsPerSession.detail"
        >
          <p className="mb-4 text-sm text-ink-soft">{t("metrics.runsPerSession.why")}</p>
          {metrics.runsPerSession.distribution.map((entry) => (
            <div
              key={entry.runs}
              className="flex items-baseline justify-between border-b border-paper-warm py-2 text-sm last:border-0"
            >
              <span className="text-ink-muted">{number(entry.runs)}</span>
              <span className="text-ink">{number(entry.sessions)}</span>
            </div>
          ))}
        </MetricCard>

        <MetricCard
          labelKey="metrics.ai"
          value={money(metrics.ai.monthEur)}
          detailKey="metrics.ai.detail"
        >
          <DetailRow labelKey="metrics.ai.previous" value={money(metrics.ai.previousMonthEur)} />
          <DetailRow labelKey="metrics.ai.refused" value={number(metrics.ai.refused)} />
          <p className="mt-4 field-label">{t("metrics.ai.feature")}</p>
          {metrics.ai.byFeature.map((entry) => (
            <div
              key={entry.feature}
              className="flex items-baseline justify-between border-b border-paper-warm py-2 text-sm last:border-0"
            >
              <span className="text-ink-muted">{entry.feature}</span>
              <span className="text-ink">{money(entry.eur)}</span>
            </div>
          ))}
        </MetricCard>
      </div>
    </SectionShell>
  );
}
